import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tabs } from 'src/design/molecules/Tabs';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { IconArrowLeft } from '@tabler/icons-react';
import { useFormik } from 'formik';
import React from 'react';
import { DailyDto, TimesheetDto } from 'src/api/FiretrolApiClient';
import { IDailySettings } from 'src/api/models/Daily';
import { WeatherCard } from 'src/app/jobs/WeatherCard';
import { toWeatherData } from 'src/app/jobs/WeatherCard.types';
import { useJobs } from 'src/hooks/useJobs';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { DatePicker } from 'src/design/atoms/DatePicker';
import { TimesheetForm } from 'src/routes/Operations/Daily/components/TimesheetForm';
import { logger, triggerDownload, formatDate } from 'src/utils';
import * as Yup from 'yup';
import { useOfficeByDivision } from 'src/api/queries/organization/useOfficeByDivision';
import { useSaveAttachmentComment } from 'src/api/queries/dailys/useDailyAttachmentMutations';
import { ImageReviewCard } from 'src/design/organisms/ImageUpload/ImageUpload';
import { useAuth } from 'src/hooks/useAuth';
import { ProgressTab } from './ProgressTab';
import { Summary } from './Summary';
import { TicketsTab } from './TicketsTab';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

const validationSchema = Yup.object().shape({
  date: Yup.mixed().required().label('Date'),
});

interface IDailyFormProps {
  initialValues: DailyDto;
  timesheets: TimesheetDto[];
  dailyId?: string;
}

export const DailyForm: React.FC<IDailyFormProps> = ({
  initialValues,
  dailyId,
  timesheets,
}) => {
  const { selectedJob, team } = useJobs();
  const { user } = useAuth();
  const { data: office, isLoading } = useOfficeByDivision(
    selectedJob?.divisionId,
  );
  const settings: IDailySettings | null = office?.dailyModules ?? null;
  const downloadAttachment = useResolveAttachmentUrl();
  const saveAttachmentComment = useSaveAttachmentComment();

  const [tab, setTab] = React.useState<string>('summary');

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {
      logger.debug('submit');
    },
  });

  if (!selectedJob || !user) return null;
  if (isLoading || !settings) return null;

  const { values, setFieldValue } = formik;

  const downloadFile = async (id: string) => {
    const result = await downloadAttachment.mutateAsync(id);
    triggerDownload(result.url, result.name);
  };

  const renderImages = () => {
    return values.attachment.map((att) => {
      return (
        <ImageReviewCard
          key={att.id}
          id={att.id}
          comment={att.comment}
          resolveImageUrl={async (id) =>
            (await downloadAttachment.mutateAsync(id)).url
          }
          onCommentSave={async (id, comment) => {
            await saveAttachmentComment.mutateAsync({ id, comment });
          }}
          onDownloadClick={() => downloadFile(att.id)}
          disabled
        />
      );
    });
  };

  return (
    <Paper>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput label="Job" disabled value={selectedJob?.name} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <DatePicker
            disabled={true}
            label="Date"
            value={values.date}
            onChange={(value) => {
              setFieldValue('date', value ?? null);
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TextInput label="Status" value={values.status} disabled />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Textarea
            label="Description of Daily Activities"
            value={values.description}
            onChange={(e) => setFieldValue('description', e.target.value)}
            disabled
            rows={4}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <WeatherCard
            job={selectedJob}
            onWeatherLoaded={(w) => setFieldValue('weather', w)}
            initialWeather={toWeatherData(values.weather)}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Tabs value={tab} onChange={(value) => setTab(value || 'summary')}>
            <Tabs.List>
              <Tabs.Tab value="summary">Daily Questions</Tabs.Tab>
              {settings.media && <Tabs.Tab value="media">Photos</Tabs.Tab>}
              {settings.tickets && (
                <Tabs.Tab value="tix">Work Tickets</Tabs.Tab>
              )}
              {settings.progress && (
                <Tabs.Tab value="progress">Physical Progress</Tabs.Tab>
              )}
              {settings.timesheets && (
                <Tabs.Tab value="timesheets">Timesheets</Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="summary" mt="md">
              {values.excludeQuestions ? (
                <Stack gap="xs">
                  <Text>No questions for {formatDate(values.date)}</Text>
                  {values.exclusionQuestionsReason && (
                    <Textarea
                      label="Reason for exclusion"
                      value={values.exclusionQuestionsReason}
                      disabled
                      rows={3}
                      readOnly
                    />
                  )}
                </Stack>
              ) : (
                <Summary
                  jobId={selectedJob.id}
                  initialQuestions={values.questions}
                  onChange={(q) => setFieldValue('questions', q)}
                  disabled
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="timesheets" mt="md">
              {values.excludeTimesheets ? (
                <Stack gap="xs">
                  <Text>No Timesheets for {formatDate(values.date)}</Text>
                  {values.exclusionTimesheetsReason && (
                    <Textarea
                      label="Reason for exclusion"
                      value={values.exclusionTimesheetsReason}
                      disabled
                      rows={3}
                      readOnly
                    />
                  )}
                </Stack>
              ) : (
                selectedJob && (
                  <TimesheetForm
                    timesheets={timesheets}
                    team={team}
                    selectedDate={values.date}
                    selectedJob={selectedJob}
                    onTimesheetChanged={console.log}
                  />
                )
              )}
            </Tabs.Panel>

            <Tabs.Panel value="progress" mt="md">
              {values.excludeProgress ? (
                <Stack gap="xs">
                  <Text>
                    No Physical Progress for {formatDate(values.date)}
                  </Text>
                  {values.exclusionProgressReason && (
                    <Textarea
                      label="Reason for exclusion"
                      value={values.exclusionProgressReason}
                      disabled
                      rows={3}
                      readOnly
                    />
                  )}
                </Stack>
              ) : (
                <ProgressTab
                  jobId={selectedJob.id}
                  dailyId={dailyId}
                  progressItems={values.dailyProgressItem}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="tix" mt="md">
              {values.excludeTickets && (
                <Stack gap="xs">
                  <Text>No Work Tickets for {formatDate(values.date)}</Text>
                  {values.exclusionTicketsReason && (
                    <Textarea
                      label="Reason for exclusion"
                      value={values.exclusionTicketsReason}
                      disabled
                      rows={3}
                      readOnly
                    />
                  )}
                </Stack>
              )}
              {!values.excludeTickets && (
                <TicketsTab
                  initialTickets={values.workTicket}
                  onChange={(tickets) => setFieldValue('workTicket', tickets)}
                  dailyId={dailyId}
                  selectedJob={selectedJob}
                  disabled
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="media" mt="md">
              {values.excludeImages ? (
                <Stack gap="xs">
                  <Text>No Images for {formatDate(values.date)}</Text>
                  {values.exclusionImagesReason && (
                    <Textarea
                      label="Reason for exclusion"
                      value={values.exclusionImagesReason}
                      disabled
                      rows={3}
                      readOnly
                    />
                  )}
                </Stack>
              ) : (
                renderImages()
              )}
            </Tabs.Panel>
          </Tabs>
          <Stack mt="xl">
            <Box>
              <LinkButton
                to={`/jobs/${selectedJob?.id}/dailys`}
                leftSection={<IconArrowLeft size={16} />}
                rightSection={undefined}
              >
                Back to Dailys
              </LinkButton>
            </Box>
          </Stack>
        </Grid.Col>
      </Grid>
    </Paper>
  );
};
